import produce from 'immer';
import { get, maxBy } from 'lodash';
import createReducer from 'shared/utils/createReducer';

import { LOGIN } from 'modules/auth/actions/login';
import { LOGOUT } from 'modules/auth/actions/logout';
import { USER_UPDATED } from 'modules/common/actions/onUserUpdates';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { DISCONNECTED } from 'modules/common/constants/conversationStatuses';
import { FETCH_APPOINTMENT } from 'modules/appointmentInfo/actions/fetchAppointment';
import { HIDE_APPOINTMENT_INFO } from 'modules/queues/common/actions/appointmentInfo';
import { UNAVAILABLE, AVAILABLE } from 'modules/common/constants/userAvailabilityTypes';
import { CUSTOMER, PROVIDER } from 'modules/common/constants/conversationParticipantsTypes';
import { ADD_AVAILABILITY_TO_SCOPE } from 'modules/queues/common/actions/onAvailabilityUpdates';
import { ADD_SERVICE_CONFIG_TO_SCOPE } from 'modules/queues/common/actions/onServiceConfigUpdates';
import { SET_SELECTED_APPOINTMENT_ID } from 'modules/appointmentInfo/actions/setSelectedAppointment';
import { STOP_CONSULTATION, STOP_CONSULTATION_LOCAL } from 'modules/conference/actions/stopConsultation';
import { ADD_GROUP_APPOINTMENT_TO_SCOPE } from 'modules/queues/common/actions/onGroupAppointmentsUpdates';
import { CREATE_CONVERSATION } from 'modules/conference/actions/_newConversationActions/createConversation';
import { WS_UPDATE_CONVERSATION } from 'modules/conference/actions/_newConversationActions/onConversationUpdate';
import { FETCH_GROUPED_APPOINTMENT } from 'modules/appointmentInfo/actions/updateAppointmentGroupCurrentAppointment';
import { UPDATE_CONVERSATION as API_UPDATE_CONVERSATION } from 'modules/conference/actions/_newConversationActions/updateConversation';


const defaultState = {
  requestStatus: requestsStatuses.default,
  fetchGroupedStatus: requestsStatuses.default,
  conversations: [],
};

export default createReducer(defaultState, {
  [FETCH_GROUPED_APPOINTMENT.pending]: state => ({
    ...state,
    fetchGroupedStatus: requestsStatuses.pending,
  }),

  [FETCH_GROUPED_APPOINTMENT.success]: state => ({
    ...state,
    fetchGroupedStatus: requestsStatuses.success,
  }),

  [FETCH_APPOINTMENT.failure]: (state) => ({
    ...state,
    requestStatus: requestsStatuses.failure,
  }),

  [FETCH_APPOINTMENT.pending]: (state) => ({
    ...state,
    requestStatus: requestsStatuses.pending,
  }),

  [FETCH_APPOINTMENT.success]: (state, { payload: { appointment } }) => ({
    ...state,
    requestStatus: requestsStatuses.success,
    ...appointment,
  }),

  [CREATE_CONVERSATION.success]: (state, { payload: { conversation } }) => ({
    ...state,
    conversations: [conversation, ...state.conversations],
  }),

  [WS_UPDATE_CONVERSATION]: (state, { payload: { conversationUpdate } }) => {
    const index = state.conversations.findIndex(
      item => item.conversationId === conversationUpdate.conversationId,
    );
    return {
      ...state,
      conversations: [
        ...state.conversations.slice(0, index),
        { ...state.conversations[index], status: conversationUpdate.conversationStatus, updatedAt: conversationUpdate.updatedAt },
        ...state.conversations.slice(index + 1),
      ],
    };
  },

  [API_UPDATE_CONVERSATION.success]: (state, { payload: { conversation } }) => {
    const index = state.conversations.findIndex(
      item => item.conversationId === conversation.conversationId,
    );
    return {
      ...state,
      conversations: [
        ...state.conversations.slice(0, index),
        { ...conversation },
        ...state.conversations.slice(index + 1),
      ],
    };
  },

  [ADD_GROUP_APPOINTMENT_TO_SCOPE]: (state, { payload }) => {
    if (!state.group) return state;
    if (payload.appointments.find(i => i.appointmentId === state.appointmentId).status !== state.status) return state;

    return {
      ...state,
      group: state.group.groupId === payload.groupId
        ? payload
        : state.group,
    };
  },

  [STOP_CONSULTATION_LOCAL]: (state) => {
    const conversation = maxBy(state.conversations, 'conversationId');
    const index = state.conversations.findIndex(item => item.conversationId === conversation.conversationId);

    return {
      ...state,
      conversations: [
        ...state.conversations.slice(0, index),
        {
          ...state.conversations[index],
          status: DISCONNECTED,
          statusHistory: [
            ...state.conversations[index].statusHistory,
            { status: DISCONNECTED, createdAt: new Date() },
          ],
        },
        ...state.conversations.slice(index + 1),
      ],
    };
  },

  [STOP_CONSULTATION.success]: (state, { payload: { status } }) => {
    const conversation = maxBy(state.conversations, 'conversationId');
    const index = state.conversations.findIndex(item => conversation && item.conversationId === conversation.conversationId);

    if (index === -1) return state;

    return {
      ...state,
      conversations: [
        ...state.conversations.slice(0, index),
        { ...state.conversations[index], status },
        ...state.conversations.slice(index + 1),
      ],
    };
  },

  [ADD_SERVICE_CONFIG_TO_SCOPE]: (state, { payload: { serviceId, config } }) => {
    if (get(state, 'service.serviceId') === serviceId) {
      return {
        ...state,
        service:
          {
            ...state.service,
            config: {
              ...state.service.config,
              ...config,
            },
          },
      };
    }

    return state;
  },

  [ADD_AVAILABILITY_TO_SCOPE]: (state, { payload: { userId, userAvailability, serviceIds } }) => {
    if (!state.participants) return state;

    if (state.participants.some(participant => participant.user.userId === userId && participant.userAvailability)) {
      return produce(state, draft => {
        const index = state.participants.findIndex(participant => participant.user.userId === userId);

        if (draft.participants[index].participantType === CUSTOMER) {
          draft.participants[index].userAvailability = userAvailability;
        }

        if (draft.participants[index].participantType === PROVIDER &&
          (serviceIds.includes(state.service.serviceId) || (userAvailability && userAvailability.status === UNAVAILABLE))) {
          draft.participants[index].userAvailability = userAvailability;
        }

        if (draft.participants[index].participantType === PROVIDER &&
          (!serviceIds.includes(state.service.serviceId) && (userAvailability && userAvailability.status === AVAILABLE))) {
          draft.participants[index].userAvailability = { status: UNAVAILABLE };
        }
      });
    }

    return state;
  },

  [USER_UPDATED]: (state, { payload: { userId, profile } }) => {
    if (!state.participants) return state;
    return {
      ...state,
      participants: state.participants.map(participant =>
        participant.user.userId === userId
          ? produce(participant, draft => {
            draft.user.profile = profile;
          })
          : participant,
      ),
      subjects: state.subjects.map(subject =>
        subject.user.userId === userId
          ? produce(subject, draft => {
            draft.user.profile = profile;
          })
          : subject,
      ),
    };
  },

  [HIDE_APPOINTMENT_INFO]: (state) => ({
    ...state,
    fetchGroupedStatus: requestsStatuses.default,
    requestStatus: requestsStatuses.default,
  }),

  [SET_SELECTED_APPOINTMENT_ID]: (state, { payload: { appointmentId } }) => ({
    ...state,
    currentAppointmentId: appointmentId,
  }),

  [LOGIN.success]: () => defaultState,
  [LOGOUT.success]: () => defaultState,
  [LOGOUT.failure]: () => defaultState,
});
